import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronRight, ChevronLeft } from "lucide-react";

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function HeroSlider({ slides }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef(null);

  const goToSlide = useCallback((index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 800);
  }, [currentSlide, isAnimating]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  // Auto-play
  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [nextSlide, slides.length]);

  if (slides.length === 0) return null;

  const slide = slides[currentSlide];

  return (
    <section 
      ref={containerRef}
      className="hero-slider relative h-[70vh] md:h-[80vh] overflow-hidden bg-[#0a0a0a]" 
      data-testid="hero-section"
    >
      {/* All slides stacked */}
      {slides.map((s, index) => (
        <div 
          key={s.slide_id || index}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Image with subtle zoom */}
          <div 
            className="absolute inset-0"
            style={{
              animation: index === currentSlide ? 'heroZoom 6s ease-out forwards' : 'none',
            }}
          >
            <img
              src={s.image_url}
              alt={s.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <div key={currentSlide}>
              {/* Badge */}
              <div 
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-sm border border-white/10 mb-5"
                style={{
                  animation: 'fadeInUp 0.6s ease-out forwards',
                  animationDelay: '0.1s',
                  opacity: 0,
                }}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
                </span>
                <span className="font-medium">Barraca Maldonado</span>
              </div>
              
              {/* Title */}
              <h1 
                className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight mb-5"
                style={{
                  animation: 'fadeInUp 0.6s ease-out forwards',
                  animationDelay: '0.2s',
                  opacity: 0,
                }}
              >
                {slide.title}
              </h1>
              
              {/* Subtitle */}
              {slide.subtitle && (
                <p 
                  className="text-base sm:text-lg md:text-xl text-white/70 max-w-lg leading-relaxed mb-7"
                  style={{
                    animation: 'fadeInUp 0.6s ease-out forwards',
                    animationDelay: '0.3s',
                    opacity: 0,
                  }}
                >
                  {slide.subtitle}
                </p>
              )}
              
              {/* CTAs */}
              <div 
                className="flex flex-col sm:flex-row gap-3"
                style={{
                  animation: 'fadeInUp 0.6s ease-out forwards',
                  animationDelay: '0.4s',
                  opacity: 0,
                }}
              >
                {slide.cta_text && slide.cta_link && (
                  <Link to={slide.cta_link}>
                    <Button 
                      size="lg" 
                      className="bg-accent hover:bg-accent/90 text-white font-semibold h-14 px-8 text-base rounded-lg group w-full sm:w-auto"
                    >
                      {slide.cta_text}
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </Link>
                )}
                <a href="https://wa.me/59899179773" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold h-14 px-6 text-base rounded-lg w-full group"
                  >
                    <WhatsAppIcon className="w-6 h-6 mr-3" />
                    <span className="flex flex-col items-start leading-tight text-left">
                      <span className="text-[10px] opacity-80 uppercase tracking-wider">Atención</span>
                      <span className="font-bold text-base">24 HORAS</span>
                    </span>
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Desktop */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            disabled={isAnimating}
            className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full items-center justify-center text-white transition-all duration-300 disabled:opacity-30"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            disabled={isAnimating}
            className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full items-center justify-center text-white transition-all duration-300 disabled:opacity-30"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Traditional Dots Navigation */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isAnimating}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-white scale-100' 
                  : 'bg-white/40 hover:bg-white/60 scale-90'
              }`}
              aria-label={`Ir al slide ${index + 1}`}
            />
          ))}
          
          {/* Mobile arrows */}
          <div className="flex md:hidden ml-4 gap-1">
            <button
              onClick={prevSlide}
              disabled={isAnimating}
              className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextSlide}
              disabled={isAnimating}
              className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}
